"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import Icon from "@/components/Icon";
import { calcROI, type RoiResult, ROI_PROJECTS } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const PROJECT_OPTIONS = ROI_PROJECTS.map((p) => ({
  value: p.id,
  label: `${p.name} — avg ${p.recoveryPct}% ROI`,
}));

const CATEGORY_LABELS: Record<string, string> = {
  exterior: "Exterior",
  interior: "Interior",
  system:   "System",
};

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [projectId, setProjectId] = useState("vinyl_windows");
  const [projectCost, setProjectCost] = useState("");
  const [homeValue, setHomeValue] = useState("");
  const [result, setResult] = useState<RoiResult | null>(null);

  const currentProject = ROI_PROJECTS.find((p) => p.id === projectId)!;

  function handleCalculate() {
    setResult(calcROI({
      projectId,
      projectCost: parseFloat(projectCost) || currentProject.avgCost,
      homeValue:   parseFloat(homeValue) || undefined,
    }));
  }

  const spec = CALCULATOR_SPECS.roi;

  return (
    <CalculatorShell
      name="Remodeling ROI Calculator"
      description="See how much value any home improvement project adds at resale, based on the NAR/NARI 2025 Remodeling Impact Report."
      icon="roi"
      category="high-value"
      accentColor="#34d399"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="ROI percentages are national averages from the NAR/NARI 2025 Remodeling Impact Report. Local market conditions vary significantly. Consult a licensed real estate appraiser for personalized guidance."
      inputsPanel={
        <div className="space-y-4">
          <SelectField id="project-type" label="Project Type" value={projectId} onChange={(e) => { setProjectId(e.target.value); setProjectCost(""); setResult(null); }} options={PROJECT_OPTIONS} />
          {/* Live project preview */}
          <div className="rounded-xl p-4" style={{ background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.20)" }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>{CATEGORY_LABELS[currentProject.category]}</span>
              <span className="text-xs font-bold" style={{ color: "#34d399" }}>{currentProject.recoveryPct}% avg ROI</span>
            </div>
            <div className="flex gap-4 text-sm">
              <div><span style={{ color: "var(--text-muted)" }}>Avg cost: </span><span className="font-semibold" style={{ color: "var(--text-base)" }}>{fmt(currentProject.avgCost)}</span></div>
              <div><span style={{ color: "var(--text-muted)" }}>Joy score: </span><span className="font-semibold" style={{ color: "var(--text-base)" }}>{currentProject.joyScore}/10</span></div>
            </div>
          </div>
          <InputField id="project-cost" label="Your Planned Budget" unit="$" type="number" min="0" step="500" placeholder={String(currentProject.avgCost)} value={projectCost} onChange={(e) => { setProjectCost(e.target.value); setResult(null); }} hint={`NAR avg for this project: ${fmt(currentProject.avgCost)} — leave blank to use this`} />
          <InputField id="home-value" label="Current Home Value (optional)" unit="$" type="number" min="0" step="5000" placeholder="400000" value={homeValue} onChange={(e) => { setHomeValue(e.target.value); setResult(null); }} hint="Used to show what % of your home's value you're investing" />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Recovery at Resale" value={`${result.recoveryPct}%`} highlight size="lg" />
              <ResultCard label="Joy Score" value={`${result.joyScore}/10`} accent size="lg" sub="Homeowner satisfaction" />
            </div>
            <ResultCard label="Estimated Value Recovered" value={fmt(result.valueRecovered)} highlight size="lg" sub={`Of your ${fmt(parseFloat(projectCost) || currentProject.avgCost)} investment`} />
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Investment Summary
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: "Total project cost",        value: fmt(parseFloat(projectCost) || currentProject.avgCost) },
                  { label: "Value recovered at resale", value: fmt(result.valueRecovered), green: true },
                  { label: "Cost not recovered (net)",  value: fmt(result.costNotRecovered), red: true },
                  ...(result.homeValuePct !== undefined ? [{ label: "% of home value invested", value: `${result.homeValuePct}%`, warn: result.homeValueWarning }] : []),
                ].map((row: { label: string; value: string; green?: boolean; red?: boolean; warn?: boolean }, i) => {
                  const isHighlight = row.green;
                  const isWarn = row.warn || row.red;
                  return (
                    <div
                      key={i}
                      className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                        isWarn 
                          ? "border-l-3 border-l-[var(--rust)] pl-2 bg-[var(--surface-muted)]/30" 
                          : isHighlight 
                            ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/10"
                            : "border-l-3 border-l-transparent"
                      }`}
                    >
                      <span className={`text-[12.5px] font-sans ${isWarn ? "text-[var(--rust)] font-semibold" : isHighlight ? "text-[var(--accent)] font-semibold" : "text-[var(--ink-soft)] font-semibold"}`}>
                        {row.label}
                      </span>
                      <span className={`font-mono text-[13px] ${isWarn ? "text-[var(--rust)] font-bold" : isHighlight ? "text-[var(--accent)] font-bold" : "text-[var(--ink)] font-bold"}`}>
                        {row.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {result.homeValueWarning && (
              <div className="disclaimer-box flex items-start gap-2">
                <Icon name="warning" className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This investment exceeds 15% of your home&apos;s value. Experts recommend keeping major remodels within 10–15% of home value to protect against over-improvement.</span>
              </div>
            )}
            <div className="info-box flex items-start gap-2">
              <Icon name="info" className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>Source:</strong> NAR/NARI 2025 Remodeling Impact Report — nar.realtor. National averages. Your market may vary significantly.</span>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">How the NAR/NARI ROI Data Works</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            The <strong>{spec.buyingGuide.narReport2025.value}</strong> is an annual joint publication by the National Association of Realtors and the National Association of the Remodeling Industry. It surveys Realtors on how much a given renovation project increases a home&apos;s resale price, and surveys homeowners on how satisfied they are with the result in their daily life. The two key metrics are: (1) <strong>Cost Recovery %</strong> — the percentage of project cost recovered in the home&apos;s sale price; and (2) the <strong>Joy Score</strong> — a <strong>{spec.buyingGuide.joyScoreExplained.value}</strong> measuring daily quality-of-life improvement. Understanding how <Link href="/articles/renovations-add-resale-value/" className="text-[var(--accent)] hover:underline font-semibold">renovations add resale value</Link> can help you decide which upgrades are worth the investment from a financial or livability standpoint.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/roi_buying_guide.webp" alt="Before and after comparison of a home renovation showing increased curb appeal and value" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/roi_site_prep.webp" alt="Real estate appraiser reviewing home renovation documentation and comparable sales data" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Over-Improvement Risk &amp; Appraiser Methodology</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            One of the most important — and most misunderstood — concepts in home renovation finance is over-improvement. <strong>{spec.sitePrep.appraiserNote.value}</strong>. This means that the renovation budget you spend has no direct relationship to what an appraiser will determine your home is worth. The NAR recommends keeping any single project within <strong>{spec.buyingGuide.overImprovementRisk.value}</strong> of your home&apos;s market value. To prepare your home correctly, refer to our <Link href="/articles/pre-listing-home-inspection-checklist/" className="text-[var(--accent)] hover:underline font-semibold">pre-listing home inspection checklist</Link> and learn about the <Link href="/articles/best-time-for-renovations/" className="text-[var(--accent)] hover:underline font-semibold">best time of year for renovations</Link> to ensure you don&apos;t over-invest.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.realtorNarJoyTop.standardCode ?? "NAR 2025 Remodeling Impact Report"}
        sourceName={spec.industryStandards.realtorNarJoyTop.sourceName}
        sourceUrl={spec.industryStandards.realtorNarJoyTop.sourceUrl}
        paragraphs={[
          <p key="p1">
            The NAR/NARI 2025 Remodeling Impact Report identifies which projects make homeowners happiest in their day-to-day lives — independent of resale impact. The top-ranked projects by Joy Score are: <b>{spec.industryStandards.realtorNarJoyTop.value}</b>. These scores reflect the profound difference that certain renovations make to daily life quality, even when their financial ROI is more modest.
          </p>,
          <p key="p2">
            Interestingly, some of the highest financial ROI projects — like adding insulation or replacing garage doors — rank lower on Joy Score because they are invisible infrastructure improvements. Meanwhile, kitchen remodels and bathroom renovations score both high financial ROI (60% and 50%, respectively) and extremely high Joy Scores (10/10 and 9.8/10), making them the strongest all-around investment for most homeowners. When planning your renovation budget, weigh both dimensions: how much will this add to my daily life, and how much will this protect my financial investment at resale?
          </p>
        ]}
        keyRequirements={[
          "Top projects by Joy Score (10/10) include painting, flooring, and kitchen upgrades.",
          "Balance daily quality-of-life enhancements against estimated financial resale returns.",
          "Infrastructure upgrades (insulation) yield high resale ROI but lower immediate Joy Scores.",
          "Maintain total single-project budget below 15% of the total home value to avoid over-improvement."
        ]}
      />

      <FaqSection calculatorId="roi" />
      <RelatedCalculators calculatorId="roi" />
    </CalculatorShell>
  );
}
